import React, { memo, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import get from 'lodash/get'
import EditIcon from 'transfix-icons/build/EditIcon'
import SidePanel from 'transfix-ui/components/SidePanel/SidePanel'
import { Box, createStyles, Grid, makeStyles, Typography } from 'transfix-ui/core'
import {
  IConnectionConfigEnvironment,
  IConnectionConfigTypes,
  IConnectionTypes,
  IOutboundConnectionConfig,
  Maybe,
} from 'types/graphqlTypes'
import { brandColors, Card, shadows, IconButton, Spinner, theme } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { useModalState, useUserDetails } from 'utils/hooks'
import { ActiveStatus } from '../../ActiveStatus'
import { ConnectionConfigDetailsGrid } from '../ConnectionConfigGrids/ConnectionConfigDetailsGrid'
import { EdiRoutingTable } from '../EdiRoutingTable/EdiRoutingTable'
import { TestConnectionConfig } from '../TestConnectionConfig'
import { useEdiOutboundConnectionConfigLazyQuery } from './graphql/EdiOutboundConnectionConfig'
import { EdiOutboundConnectionConfigModal } from './modals/EdiOutboundConnectionConfigModal/EdiOutboundConnectionConfigModal'
import { TEdiOutboundConnectionConfigModalSchema } from './schema/EdiOutboundConnectionConfigModalSchema'

interface IOutboundConnectionConfigDetailsProps {
  onSidebarClose: () => void
  onEdit: (options: TEdiOutboundConnectionConfigModalSchema) => void
}

const useOutboundConnectionConfigDetailsStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
      boxShadow: shadows[3],
    },
    titleText: {
      display: 'flex',
      flex: theme.spacing(1),
      alignItems: 'center',
    },
    secondary: {
      color: brandColors.coolGray5,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      marginLeft: theme.spacing(1),
    },
  })
)

type TableCellLabelValueList = {
  label: string
  value: Maybe<string | number | boolean>
}[]

const getCells = (
  outboundConnectionConfigDetails: IOutboundConnectionConfig
): TableCellLabelValueList => {
  const {
    as2ConnectionConfig,
    componentSeparator,
    connectionType,
    createdAt,
    destinationOverride,
    elementSeparator,
    environment,
    expect997s,
    groupVersionStr,
    gsReceiverId,
    gsSenderId,
    host,
    interchangeVersionStr,
    isaReceiverId,
    isaReceiverIdQualifier,
    isaSenderId,
    isaSenderIdQualifier,
    pathPrefix,
    port,
    repetitionSeparator,
    secretKey,
    segmentSeparator,
    testIndicator,
    tmsName,
    username,
  } = outboundConnectionConfigDetails || {}

  const commonFields = [
    { label: 'Created At', value: createdAt },
    { label: 'Connection Type', value: connectionType },
    { label: 'Environment', value: capitalize(environment) },
    { label: 'Interchange Version', value: interchangeVersionStr },
    { label: 'Group Version', value: groupVersionStr },
    { label: 'ISA Receiver ID', value: isaReceiverId },
    { label: 'ISA Receiver ID Qualifier', value: isaReceiverIdQualifier },
    { label: 'ISA Sender ID', value: isaSenderId },
    { label: 'ISA Sender ID Qualifier', value: isaSenderIdQualifier },
    { label: 'GS Receiver ID', value: gsReceiverId },
    { label: 'GS Sender ID', value: gsSenderId },
    { label: 'ISA-15', value: testIndicator === 'TEST' ? 'T' : 'P' },
    { label: 'Destination Override', value: destinationOverride },
    { label: 'Segment', value: segmentSeparator },
    { label: 'Element', value: elementSeparator },
    { label: 'Component', value: componentSeparator },
    { label: 'Repetition', value: repetitionSeparator },
    { label: 'Expect 997s', value: expect997s },
  ]

  if (connectionType === IConnectionTypes.AS2) {
    return [
      ...commonFields,
      { label: 'AS2 Connection Config ID', value: as2ConnectionConfig?.id },
      { label: 'Identifier', value: as2ConnectionConfig?.identifier },
    ]
  }

  return [
    ...commonFields,
    { label: 'Host', value: host },
    { label: 'Port', value: port },
    { label: 'Username', value: username },
    { label: 'Path Prefix', value: pathPrefix },
    { label: 'TMS Name', value: tmsName },
    { label: 'Secret Key', value: secretKey },
  ]
}

export const OutboundConnectionConfigDetails = memo<IOutboundConnectionConfigDetailsProps>(
  ({ onEdit, onSidebarClose }) => {
    const { configId: outboundConnectionConfigId } = useParams<{ configId: string }>()

    const classes = useOutboundConnectionConfigDetailsStyles()
    const { userPermissions } = useUserDetails()
    const {
      canAccessAs2ConnectionConfigs,
      canAccessEdiMapperConnectionConfigs,
      canAccessSelfServeInboundParser,
      canAccessSelfServeOutboundBuilder,
    } = userPermissions || {}
    const userHasEdiIntegrationSpecialistRole =
      canAccessAs2ConnectionConfigs &&
      canAccessEdiMapperConnectionConfigs &&
      canAccessSelfServeInboundParser &&
      canAccessSelfServeOutboundBuilder

    const { createToggleHandler, modalState } = useModalState({
      dispatchEditModal: false,
    })

    const openEditHandler = createToggleHandler('dispatchEditModal', true)
    const closeEditHandler = createToggleHandler('dispatchEditModal', false)

    const [
      getEdiOutboundConnectionConfig,
      { data, loading },
    ] = useEdiOutboundConnectionConfigLazyQuery({
      variables: {
        id: String(outboundConnectionConfigId),
      },
      notifyOnNetworkStatusChange: true,
    })

    /* Loading the configs when page loads */
    useEffect(() => {
      getEdiOutboundConnectionConfig()
    }, [getEdiOutboundConnectionConfig])

    const { ediOutboundConnectionConfig } = data || {}

    const {
      as2ConnectionConfig,
      componentSeparator,
      connectionType,
      destinationOverride,
      disabled,
      elementSeparator,
      environment,
      expect997s,
      groupVersionStr,
      gsReceiverId,
      gsSenderId,
      host,
      id,
      interchangeVersionStr,
      isaReceiverId,
      isaReceiverIdQualifier,
      isaSenderId,
      isaSenderIdQualifier,
      partnerApplication,
      partnerApplicationId,
      partnerApplicationType,
      pathPrefix,
      port,
      repetitionSeparator,
      secretKey,
      segmentSeparator,
      testIndicator,
      username,
    } = ediOutboundConnectionConfig || {}

    const partnerApplicationTypeFormatted =
      partnerApplicationType === 'ShipperApplication' ? 'Shipper' : 'Carrier'

    const cells = useMemo(
      () => getCells(ediOutboundConnectionConfig as IOutboundConnectionConfig),
      [ediOutboundConnectionConfig]
    )

    const company = useMemo(() => {
      return {
        id: partnerApplicationId,
        text: get(partnerApplication, 'shipperName', get(partnerApplication, 'carrierName', '')),
        data: {
          __typename: partnerApplicationType,
        },
      }
    }, [partnerApplication, partnerApplicationId, partnerApplicationType])

    const currentValues = useMemo(() => {
      return {
        id: id || null,
        isDisabled: disabled === null ? true : disabled,
        company: company || {},
        connectionType: connectionType || IConnectionTypes.FTP,
        environment: environment || IConnectionConfigEnvironment.staging,
        host: host || '',
        partnerApplicationId: partnerApplicationId || null,
        pathPrefix: pathPrefix || '',
        secretKey: secretKey || '',
        as2ConnectionConfigId: as2ConnectionConfig?.id,
        port: port || 0,
        username: username || '',
        destinationOverride: destinationOverride || '',
        segmentSeparator: segmentSeparator || '',
        componentSeparator: componentSeparator || '',
        elementSeparator: elementSeparator || '',
        groupVersion: groupVersionStr || '',
        gsReceiverId: gsReceiverId || '',
        gsSenderId: gsSenderId || '',
        interchangeVersion: interchangeVersionStr || '',
        isaReceiverId: isaReceiverId || '',
        isaReceiverIdQualifier: isaReceiverIdQualifier || '',
        isaSenderId: isaSenderId || 'TFXH',
        isaSenderIdQualifier: isaSenderIdQualifier || '02',
        repetitionSeparator: repetitionSeparator || '',
        testIndicator: testIndicator || '',
        expect997s: expect997s === null ? true : expect997s,
      }
    }, [
      as2ConnectionConfig?.id,
      company,
      componentSeparator,
      connectionType,
      destinationOverride,
      disabled,
      elementSeparator,
      environment,
      groupVersionStr,
      gsReceiverId,
      gsSenderId,
      host,
      id,
      interchangeVersionStr,
      isaReceiverId,
      isaReceiverIdQualifier,
      isaSenderId,
      isaSenderIdQualifier,
      partnerApplicationId,
      pathPrefix,
      port,
      repetitionSeparator,
      secretKey,
      segmentSeparator,
      testIndicator,
      username,
      expect997s,
    ])

    return (
      <SidePanel anchor='right' onClose={onSidebarClose} PaperProps={{ style: { width: '86%' } }}>
        {loading && <Spinner />}
        <Box data-test='SidePanel' mb={3}>
          <Box display='flex' justifyContent='center' alignItems='center' pl={2} pr={2}>
            <Typography variant='h2' className={classes.titleText}>
              <span data-test='inbound-connection-config-id-header'>{id}</span>
              <span className={classes.secondary}>|</span>
              <span className='mr3' data-test='inbound-connection-config-identifier-header'>
                {`${partnerApplicationTypeFormatted} - ${partnerApplicationId}`}
              </span>
              <span data-test='inbound-connection-config-active-status'>
                <ActiveStatus disabled={disabled} />
              </span>
              <span>
                <TestConnectionConfig
                  configId={id!}
                  connectionType={connectionType}
                  connectionConfigType={IConnectionConfigTypes.OutboundEdiMapperConnectionConfig}
                />
              </span>
            </Typography>
          </Box>
        </Box>
        <hr />
        <Grid container justifyContent='flex-start' spacing={1}>
          <Grid item lg={8} sm={12} xs={12} md={12}>
            <Card className={classes.card}>
              <div className={classes.header}>
                <h3>Connection Details</h3>
                {userHasEdiIntegrationSpecialistRole && (
                  <IconButton
                    Icon={EditIcon}
                    color='primary'
                    label='Edit Inbound Connection Config'
                    onClick={openEditHandler}
                  />
                )}
              </div>
              <hr />
              {cells.map(cell => (
                <ConnectionConfigDetailsGrid key={`cell-${id}-${cell.label}`} gridHash={cell} />
              ))}
            </Card>
            {ediOutboundConnectionConfig && (
              <Card className={classes.card}>
                <div className={classes.header}>
                  <h3>Edi Routing Configuration</h3>
                </div>
                <hr />
                <EdiRoutingTable
                  connectionConfigId={ediOutboundConnectionConfig.id}
                  connectionConfigType='OutboundConnectionConfig'
                />
              </Card>
            )}
          </Grid>
        </Grid>
        {modalState.dispatchEditModal && (
          <EdiOutboundConnectionConfigModal
            title='Edit Outbound Connection Config'
            open={modalState.dispatchEditModal}
            defaultValues={currentValues}
            onClose={closeEditHandler}
            onSubmit={onEdit}
          />
        )}
      </SidePanel>
    )
  }
)
